import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * The two places in a Vorgang's head that a page fills (Task 9, UX-Gutachten
 * 1, B-1/B-4): `actions` — the way on and „Mehr …“ at the right of the
 * Vorgang's name — and `path`, the stage path beside the tabs.
 */
export type CaseHeadSlot = "actions" | "path";

export interface CaseHead {
  actions: HTMLElement | null;
  path: HTMLElement | null;
  /** The head's `h1`, the Vorgang's title — where the record puts a person down on opening it. */
  heading: HTMLHeadingElement | null;
  /**
   * Whether the frame draws its head at all. A Vorgang that is gone draws
   * none (`CaseFrame`), and then no `h1` stands above the page — its own
   * title is the page's `h1` again (`PageTitle`; final review, M-15).
   */
  shown: boolean;
  setActions: (element: HTMLElement | null) => void;
  setPath: (element: HTMLElement | null) => void;
  setHeading: (element: HTMLHeadingElement | null) => void;
  setShown: (shown: boolean) => void;
}

const CaseHeadContext = createContext<CaseHead | null>(null);

/**
 * One head for every page of a Vorgang, in the frame above the page (B-4):
 * the Vorgang's number, title, meta line, tabs and stage path stand there on
 * every tab, so a tab change moves the page and leaves the Vorgang standing.
 * What only a page knows — the record's way on, its „Mehr …“ with the dialogs
 * behind it, the stage path with its gestures — the page renders itself, into
 * the head's places (`InCaseHead`): its state, its refs and its dialogs stay
 * the page's, only the boxes stand in the head.
 *
 * The shell wraps the frame and the page in this; `framed` says whether a
 * Vorgang's frame stands above the page at all. Always rendered, framed or
 * not, so the page under it keeps its place in the tree when a page of a
 * Vorgang follows one that is none (`/umriss` and `/umriss?case=…` are the
 * same screen).
 */
export function CaseHeadSlots({ framed, children }: { framed: boolean; children: ReactNode }) {
  const [actions, setActions] = useState<HTMLElement | null>(null);
  const [path, setPath] = useState<HTMLElement | null>(null);
  const [heading, setHeading] = useState<HTMLHeadingElement | null>(null);
  const [shown, setShown] = useState(true);
  const head = useMemo<CaseHead>(
    () => ({ actions, path, heading, shown, setActions, setPath, setHeading, setShown }),
    [actions, path, heading, shown],
  );
  return <CaseHeadContext.Provider value={framed ? head : null}>{children}</CaseHeadContext.Provider>;
}

/** The head of the Vorgang the page stands in — `null` on a page that stands in none. */
export function useCaseHead(): CaseHead | null {
  return useContext(CaseHeadContext);
}

/**
 * What a page puts into its Vorgang's head: there, once the frame has drawn
 * the place; in place, where no frame stands above the page (a page rendered
 * on its own — its tests, a screen outside the shell). Until the frame's place
 * is in the document it renders nothing, which is one commit: the frame is
 * drawn above the page in the same one and hands its places over before the
 * browser paints.
 */
export function InCaseHead({ slot, children }: { slot: CaseHeadSlot; children: ReactNode }) {
  const head = useCaseHead();
  if (head === null) return <>{children}</>;
  const target = head[slot];
  return target === null ? null : createPortal(children, target);
}
